import { useMemo } from "react";

import { AnchoredTooltip } from "@/components/common/AnchoredTooltip";
import { cn } from "@/lib/utils";

import type { ChartMask } from "./types";

export interface TooltipRow {
  label: string;
  value: string;
  color?: string;
}

interface ChartTooltipProps {
  /** Viewport X of the pointer anchor. */
  anchorX: number;
  /** Viewport Y of the pointer anchor. */
  anchorY: number;
  title?: string;
  rows: TooltipRow[];
  /** Blur restricted PII / money in the tooltip. */
  mask?: ChartMask;
  /** Optional footer hint (e.g. "Click to filter"). */
  hint?: string;
}

/**
 * Floating glass tooltip that follows the cursor. Portaled to `document.body`
 * so dashboard tiles and rails cannot clip it. A caret points at the pointer
 * and the box stays inside the viewport.
 */
export function ChartTooltip({
  anchorX,
  anchorY,
  title,
  rows,
  mask,
  hint,
}: ChartTooltipProps) {
  const anchor = useMemo(
    () => ({ x: anchorX, y: anchorY, width: 0, height: 0 }),
    [anchorX, anchorY],
  );

  return (
    <AnchoredTooltip
      anchor={anchor}
      pointerEvents={false}
      animateIn={false}
      className="z-[80] px-2.5 py-1.5"
      maxWidth={224}
    >
      {title && (
        <p
          className={cn(
            "mb-1 font-semibold text-foreground",
            mask?.category && "sensitive-blur",
          )}
        >
          {title}
        </p>
      )}
      <ul className="space-y-0.5">
        {rows.map((row, i) => (
          <li key={i} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              {row.color && (
                <span
                  className="inline-block size-2 shrink-0 rounded-[3px]"
                  style={{ backgroundColor: row.color }}
                />
              )}
              <span className={cn(mask?.series && "sensitive-blur")}>
                {row.label}
              </span>
            </span>
            <span
              className={cn(
                "font-medium tabular-nums text-foreground",
                mask?.value && "sensitive-blur",
              )}
            >
              {row.value}
            </span>
          </li>
        ))}
      </ul>
      {hint && (
        <p className="mt-1.5 border-t border-border/50 pt-1 text-[10px] text-muted-foreground">
          {hint}
        </p>
      )}
    </AnchoredTooltip>
  );
}
